import { useState, useEffect, Fragment } from 'react';
import { Link } from 'react-router-dom';
import { Layout } from '../../../components/layout/Layout';
import api from '../../../api';
import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import { Card, CardContent } from '../../../components/ui/card';
import { Badge } from '../../../components/ui/badge';
import {
  Search, Server, Filter, X, Link2, Cpu, MemoryStick, Monitor,
  ChevronLeft, ChevronRight, ChevronUp, ChevronDown, ChevronsUpDown,
  ChevronRight as ChevronRightIcon, HardDrive, Camera as CamIcon, Trash2
} from 'lucide-react';
import { toast } from 'sonner';
import { formatDateARG } from '../../../lib/utils';
import { useAuth } from '../../../context/AuthContext';
import { DeleteVirtDialog } from '../../../components/virt/DeleteVirtDialog';

const formatSize = (gb) => {
  if (!gb) return '-';
  const n = Number(gb);
  if (n >= 1024) return `${(n / 1024).toFixed(1)} TB`;
  return `${n.toFixed(0)} GB`;
};

const HypervHosts = () => {
  const [loading, setLoading] = useState(true);
  const [hosts, setHosts] = useState([]);
  const [stats, setStats] = useState(null);
  const [initialized, setInitialized] = useState(true);
  const [pagination, setPagination] = useState({ page: 1, page_size: 25, total: 0, pages: 1 });
  const [filters, setFilters] = useState({ search: '' });
  const [sortConfig, setSortConfig] = useState({ field: 'hostname', dir: 'asc' });
  const [expanded, setExpanded] = useState({});
  const [hostVMs, setHostVMs] = useState({});
  const [loadingVMs, setLoadingVMs] = useState({});
  const { user } = useAuth();
  const isAdmin = !!user?.is_admin;
  const colCount = isAdmin ? 9 : 8;
  const [deleteTarget, setDeleteTarget] = useState(null);

  const fetchHosts = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({
        page: pagination.page.toString(),
        page_size: pagination.page_size.toString(),
        sort_by: sortConfig.field,
        sort_dir: sortConfig.dir,
      });
      if (filters.search) params.append('search', filters.search);
      const res = await api.get(`/virt/hyperv/hosts?${params}`);
      setHosts(res.data.items);
      setInitialized(res.data.initialized !== false);
      setPagination(prev => ({ ...prev, total: res.data.total, pages: res.data.pages }));
    } catch (err) {
      toast.error('Error al cargar hosts Hyper-V');
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      const res = await api.get('/virt/hyperv/hosts/stats');
      setStats(res.data);
    } catch (err) { console.error(err); }
  };

  const fetchHostVMs = async (hostId) => {
    if (hostVMs[hostId]) return;
    setLoadingVMs(prev => ({ ...prev, [hostId]: true }));
    try {
      const res = await api.get(`/virt/hyperv/hosts/${hostId}`);
      setHostVMs(prev => ({ ...prev, [hostId]: res.data.vms || [] }));
    } catch (err) {
      toast.error('Error al cargar VMs del host');
    } finally {
      setLoadingVMs(prev => ({ ...prev, [hostId]: false }));
    }
  };

  const toggleExpand = (id) => {
    const isOpen = expanded[id];
    setExpanded(prev => ({ ...prev, [id]: !isOpen }));
    if (!isOpen) fetchHostVMs(id);
  };

  useEffect(() => { fetchStats(); }, []);
  useEffect(() => {
    const t = setTimeout(fetchHosts, 300);
    return () => clearTimeout(t);
  }, [pagination.page, filters, sortConfig]);

  const handleSort = (field) => setSortConfig(prev => ({ field, dir: prev.field === field && prev.dir === 'asc' ? 'desc' : 'asc' }));
  const SortIcon = ({ field }) => sortConfig.field !== field
    ? <ChevronsUpDown className="h-3 w-3 text-muted-foreground/50" />
    : (sortConfig.dir === 'asc' ? <ChevronUp className="h-3 w-3 text-primary" /> : <ChevronDown className="h-3 w-3 text-primary" />);
  const SortableHeader = ({ field, children, className = '' }) => (
    <th className={`cursor-pointer hover:bg-muted/70 select-none ${className}`} onClick={() => handleSort(field)}>
      <div className="flex items-center gap-1">{children}<SortIcon field={field} /></div>
    </th>
  );

  return (
    <Layout>
      <div className="space-y-6">
        <div className="page-header">
          <h1 className="page-title">Hosts Hyper-V</h1>
          <p className="page-subtitle">Servidores Hyper-V standalone (click en fila para ver sus VMs)</p>
        </div>

        {!initialized && (
          <Card className="card-subtle border-yellow-500/40">
            <CardContent className="p-4 text-sm text-yellow-500">
              Módulo Hyper-V no inicializado en esta base (aún no se ejecutó el discovery).
            </CardContent>
          </Card>
        )}

        {stats && stats.initialized !== false && (
          <div className="grid grid-cols-2 md:grid-cols-4 xl:grid-cols-7 gap-4">
            <Card className="card-subtle" data-testid="stat-hosts"><CardContent className="p-4 text-center"><p className="stats-card-value">{stats.total_hosts || 0}</p><p className="stats-card-label">Hosts</p></CardContent></Card>
            <Card className="card-subtle" data-testid="stat-vms"><CardContent className="p-4 text-center"><p className="stats-card-value">{stats.total_vms || 0}</p><p className="stats-card-label">VMs</p></CardContent></Card>
            <Card className="card-subtle" data-testid="stat-running"><CardContent className="p-4 text-center"><p className="stats-card-value text-green-500">{stats.running_vms || 0}</p><p className="stats-card-label">Encendidas</p></CardContent></Card>
            <Card className="card-subtle" data-testid="stat-without-server"><CardContent className="p-4 text-center"><p className="stats-card-value text-yellow-500">{stats.vms_without_server || 0}</p><p className="stats-card-label">VMs sin match</p></CardContent></Card>
            <Card className="card-subtle" data-testid="stat-storage-critical"><CardContent className="p-4 text-center"><p className="stats-card-value text-red-500">{stats.storage_critical || 0}</p><p className="stats-card-label">Storage ≥90%</p></CardContent></Card>
            <Card className="card-subtle" data-testid="stat-checkpoints-old"><CardContent className="p-4 text-center"><p className="stats-card-value text-yellow-500">{stats.checkpoints_old || 0}</p><p className="stats-card-label">Checkpoints &gt;7d</p></CardContent></Card>
            <Card className="card-subtle" data-testid="stat-orphan-vhds"><CardContent className="p-4 text-center"><p className="stats-card-value text-orange-500">{stats.orphan_vhds || 0}</p><p className="stats-card-label">VHD huérfanos</p></CardContent></Card>
          </div>
        )}

        <Card className="card-subtle">
          <CardContent className="p-4">
            <div className="flex items-center gap-4">
              <Filter className="h-4 w-4 text-muted-foreground" />
              <span className="text-sm font-medium">Filtros:</span>
              <div className="flex items-center gap-2 flex-1 max-w-sm">
                <Search className="h-4 w-4 text-muted-foreground" />
                <Input placeholder="Buscar por host, hardware, CPU, versión..." value={filters.search}
                  onChange={(e) => setFilters({ search: e.target.value })} className="h-9" data-testid="host-search-input" />
              </div>
              {filters.search && (
                <Button variant="ghost" size="sm" onClick={() => setFilters({ search: '' })}><X className="h-4 w-4 mr-1" /> Limpiar</Button>
              )}
            </div>
          </CardContent>
        </Card>

        <Card className="card-subtle">
          <CardContent className="p-0">
            <table className="table-subtle" data-testid="hosts-table">
              <thead className="bg-muted/50">
                <tr className="border-b">
                  <th className="w-8"></th>
                  <SortableHeader field="hostname" className="text-left">HOST</SortableHeader>
                  <SortableHeader field="hyperv_version" className="text-left">VERSIÓN HYPER-V</SortableHeader>
                  <SortableHeader field="cpu_cores" className="text-center justify-center">CPU</SortableHeader>
                  <SortableHeader field="ram_gb" className="text-center justify-center">RAM</SortableHeader>
                  <SortableHeader field="vm_count" className="text-center justify-center">VMs</SortableHeader>
                  <SortableHeader field="uptime_days" className="text-center justify-center">UPTIME</SortableHeader>
                  <SortableHeader field="last_scanned_at" className="text-left">ÚLTIMA SYNC</SortableHeader>
                  {isAdmin && <th className="text-center">ACCIONES</th>}
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr><td colSpan={colCount} className="text-center p-8 text-muted-foreground">Cargando...</td></tr>
                ) : hosts.length === 0 ? (
                  <tr><td colSpan={colCount} className="text-center p-8 text-muted-foreground">No hay hosts Hyper-V registrados</td></tr>
                ) : hosts.map(h => (
                  <Fragment key={h.id}>
                    <tr className={`border-b hover:bg-muted/30 cursor-pointer ${expanded[h.id] ? 'bg-muted/20' : ''}`}
                      onClick={() => toggleExpand(h.id)} data-testid={`host-row-${h.id}`}>
                      <td className="p-4 text-center"><ChevronRightIcon className={`h-4 w-4 transition-transform ${expanded[h.id] ? 'rotate-90' : ''}`} /></td>
                      <td className="p-4">
                        <div className="flex items-center gap-2"><Server className="h-4 w-4 text-blue-500" /><span className="font-medium">{h.hostname}</span></div>
                        <p className="text-xs text-muted-foreground ml-6 font-mono">{h.instance_host}{h.hardware_vendor ? ` · ${h.hardware_vendor}` : ''}</p>
                      </td>
                      <td className="p-4">
                        <span className="text-sm">{h.hyperv_version || '-'}</span>
                        {h.os_build && <p className="text-xs text-muted-foreground">Build: {h.os_build}</p>}
                      </td>
                      <td className="p-4 text-center">{h.cpu_cores ? `${h.cpu_cores}c / ${h.cpu_threads || '-'}t` : '-'}</td>
                      <td className="p-4 text-center">{formatSize(h.ram_gb)}</td>
                      <td className="p-4 text-center"><Badge variant="outline">{h.vms_count || 0}</Badge></td>
                      <td className="p-4 text-center">{h.uptime_days != null ? `${h.uptime_days}d` : '-'}</td>
                      <td className="p-4 text-sm text-muted-foreground">{h.last_scanned_at ? formatDateARG(h.last_scanned_at) : '-'}</td>
                      {isAdmin && (
                        <td className="p-4 text-center" onClick={(e) => e.stopPropagation()}>
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-8 w-8 text-red-500 hover:text-red-600 hover:bg-red-500/10"
                            onClick={() => setDeleteTarget({ id: h.id, name: h.hostname })}
                            data-testid={`host-delete-btn-${h.id}`}
                            title="Eliminar host"
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </td>
                      )}
                    </tr>
                    {expanded[h.id] && (
                      <tr key={`${h.id}-vms`}>
                        <td colSpan={colCount} className="bg-muted/10 p-0">
                          <div className="p-4 pl-12">
                            {loadingVMs[h.id] ? (
                              <p className="text-muted-foreground text-sm">Cargando VMs...</p>
                            ) : !hostVMs[h.id] || hostVMs[h.id].length === 0 ? (
                              <p className="text-muted-foreground text-sm">No hay VMs en este host</p>
                            ) : (
                              <div className="space-y-2">
                                <p className="text-sm font-medium mb-3">Máquinas Virtuales ({hostVMs[h.id].length})</p>
                                <table className="table-subtle">
                                  <thead className="bg-muted/30">
                                    <tr className="border-b">
                                      <th>VM</th>
                                      <th className="text-center">ESTADO</th>
                                      <th className="text-center">VCPU</th>
                                      <th className="text-center">RAM</th>
                                      <th>SERVIDOR FÍSICO</th>
                                    </tr>
                                  </thead>
                                  <tbody>
                                    {hostVMs[h.id].map(vm => (
                                      <tr key={vm.id} className="border-b border-muted/20 hover:bg-muted/20">
                                        <td className="p-2"><div className="flex items-center gap-2"><Monitor className="h-3 w-3 text-muted-foreground" /><span className="font-medium">{vm.name}</span></div></td>
                                        <td className="p-2 text-center"><Badge variant="outline" className={vm.state === 'running' ? 'text-green-500 border-green-500' : 'text-muted-foreground'}>{vm.state}</Badge></td>
                                        <td className="p-2 text-center">{vm.vcpu || '-'}</td>
                                        <td className="p-2 text-center">{vm.ram_assigned_gb ? `${Number(vm.ram_assigned_gb).toFixed(0)} GB` : '-'}</td>
                                        <td className="p-2">
                                          {vm.server_id ? (
                                            <Link to={`/assets/servers?search=${encodeURIComponent(vm.server_hostname)}`} className="flex items-center gap-1 text-blue-500 hover:underline text-sm" onClick={(e) => e.stopPropagation()}>
                                              <Link2 className="h-3 w-3" />{vm.server_hostname}
                                            </Link>
                                          ) : <span className="text-muted-foreground text-sm">-</span>}
                                        </td>
                                      </tr>
                                    ))}
                                  </tbody>
                                </table>
                              </div>
                            )}
                          </div>
                        </td>
                      </tr>
                    )}
                  </Fragment>
                ))}
              </tbody>
            </table>
          </CardContent>
        </Card>

        {pagination.pages > 1 && (
          <div className="flex justify-center items-center gap-4">
            <Button variant="outline" size="sm" disabled={pagination.page === 1} onClick={() => setPagination(prev => ({ ...prev, page: prev.page - 1 }))}><ChevronLeft className="h-4 w-4" /></Button>
            <span className="text-sm text-muted-foreground">Página {pagination.page} de {pagination.pages} ({pagination.total} hosts)</span>
            <Button variant="outline" size="sm" disabled={pagination.page === pagination.pages} onClick={() => setPagination(prev => ({ ...prev, page: prev.page + 1 }))}><ChevronRight className="h-4 w-4" /></Button>
          </div>
        )}

        <DeleteVirtDialog
          open={!!deleteTarget}
          onOpenChange={(v) => { if (!v) setDeleteTarget(null); }}
          previewUrl={deleteTarget ? `/virt/hyperv/hosts/${deleteTarget.id}/delete-preview` : null}
          deleteUrl={deleteTarget ? `/virt/hyperv/hosts/${deleteTarget.id}` : null}
          onDeleted={() => { setDeleteTarget(null); fetchHosts(); fetchStats(); }}
        />
      </div>
    </Layout>
  );
};

export default HypervHosts;
